// ReportsDashboard: Analytics, booking funnel, revenue trends, staff performance

import React, { useEffect, useState } from 'react';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Chip, Card, CardContent, Tooltip as MuiTooltip, useTheme, useMediaQuery } from '@mui/material';
import api from '../../services/api';
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
Chart.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);


function ReportsDashboard() {
  const [analytics, setAnalytics] = useState({});
  // Helper: get counts by status
  const bookingStatusCounts = analytics.bookings || {};
  const ticketAssignedCount = analytics.ticketAssigned || 0;
  const speakerCounts = analytics.speakerCounts || {};
  const packageTypeCounts = analytics.packageTypeCounts || {};
  const packageNameCounts = analytics.packageNameCounts || {};
  const staffCreatedCount = analytics.staffCreated || 0;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [exportDialog, setExportDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // analyticsSource: 0=All, 1=Speakers, 2=Eligible, 3=Custom
  const [analyticsSource, setAnalyticsSource] = useState(0);
  const analyticsSourceOptions = [
    { label: 'All Bookings', value: 0 },
    { label: 'Speakers Only', value: 1 },
    { label: 'Eligible Bookers', value: 2 },
    { label: 'Custom Filter', value: 3 },
  ];
  const customFilterOptions = [
    { label: 'Paid', value: 'paid' },
    { label: 'Pending', value: 'pending' },
    { label: 'Verified', value: 'verified' },
    { label: 'Package Type', value: 'packageType' },
    { label: 'Package Name', value: 'packageName' },
  ];
  const [customFilter, setCustomFilter] = useState('');
  const [customFilterValue, setCustomFilterValue] = useState('');

  // Auto-refresh analytics every 30 seconds for live updates
  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000); // 30 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [analyticsSource, startDate, endDate]);

  const fetchAnalytics = () => {
    setLoading(true);
    let params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    let endpoint = '/api/analytics';
    if (analyticsSource === 1) endpoint = '/api/bookings/approved-speakers';
    if (analyticsSource === 2) endpoint = '/api/bookings/eligible-bookers';
    if (analyticsSource === 3 && customFilter) {
      params.filter = customFilter;
      params.value = customFilterValue;
    }
    api.get(endpoint, { params })
      .then(res => {
        const data = res.data || {};
        setAnalytics({
          totalBookings: data.totalBookings ?? 0,
          bookings: data.bookings ?? {},
          ticketAssigned: data.ticketAssigned ?? 0,
          speakerCounts: data.speakerCounts ?? {},
          packageTypeCounts: data.packageTypeCounts ?? {},
          packageNameCounts: data.packageNameCounts ?? {},
          staffCreated: data.staffCreated ?? 0,
          totalRevenue: data.totalRevenue ?? 0,
          totalPayments: data.totalPayments ?? 0,
          totalStaffActions: data.totalStaffActions ?? 0,
          payments: data.payments ?? {},
          staffProcessLogs: data.staffProcessLogs ?? [],
        });
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to fetch reports'))
      .finally(() => setLoading(false));
  };

  const handleTabChange = (e, v) => setTab(v);
  const handleExport = (type) => {
    // Placeholder: implement PDF/Excel export logic here
    alert(`Exporting as ${type}`);
  };

  // Status colors for charts and badges
  const statusColors = {
    pending: '#eab308', // yellow
    approved: '#16a34a', // green
    paid: '#2563eb', // blue
    verified: '#22c55e', // bright green
    rejected: '#ef4444', // red
    failed: '#f43f5e',
    completed: '#0ea5a4',
    unpaid: '#eab308',
    processing: '#6366f1',
    sent: '#0ea5e9',
    staff_review: '#6366f1',
    available: '#0ea5a4',
    booked: '#f59e42',
    invited: '#0ea5e9',
    default: '#888'
  };
  // Improved color mixing for charts
  const chartColors = [
    '#16a34a', // green
    '#0ea5e9', // blue
    '#6366f1', // indigo
    '#eab308', // yellow
    '#f59e42', // orange
    '#f43f5e', // red
    '#0ea5a4', // teal
    '#888',    // gray
  ];

  // Chart data with color mapping
  const bookingFunnelData = analytics.bookings ? {
    labels: Object.keys(analytics.bookings),
    datasets: [{
      label: 'Bookings',
      data: Object.values(analytics.bookings),
      backgroundColor: Object.keys(analytics.bookings).map((s, i) => chartColors[i % chartColors.length]),
    }],
  } : null;
  const revenueTrendsData = analytics.payments ? {
    labels: Object.keys(analytics.payments),
    datasets: [{
      label: 'Payments',
      data: Object.values(analytics.payments),
      backgroundColor: Object.keys(analytics.payments).map((s, i) => chartColors[i % chartColors.length]),
    }],
  } : null;
  const staffPerformanceData = analytics.staffProcessLogs ? {
    labels: analytics.staffProcessLogs.map(l => l.userId),
    datasets: [{
      label: 'Actions',
      data: analytics.staffProcessLogs.map(l => l.actions),
      backgroundColor: analytics.staffProcessLogs.map((l, i) => chartColors[i % chartColors.length]),
    }],
  } : null;

  // KPI summary cards
  // Count all active bookings (approved, paid, pending, etc.)
  const activeBookingCount = Object.values(bookingStatusCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
  const fallbackSpeakerCount =
    analytics.bookings
      ? Object.entries(analytics.bookings)
          .filter(([status, count]) =>
            ['speaker', 'speakers'].includes(status.toLowerCase()) && count > 0
          )
          .reduce((a, [, count]) => a + count, 0)
      : 0;
  const kpis = [
    { label: 'Bookings', value: activeBookingCount, color: '#0ea5a4' },
    { label: 'Pending', value: bookingStatusCounts.pending ?? 0, color: '#eab308' },
    { label: 'Approved', value: bookingStatusCounts.approved ?? 0, color: '#16a34a' },
    { label: 'Paid', value: bookingStatusCounts.paid ?? 0, color: statusColors.paid },
    { label: 'Verified', value: bookingStatusCounts.verified ?? 0, color: statusColors.verified },
    { label: 'Rejected', value: bookingStatusCounts.rejected ?? 0, color: statusColors.rejected },
    { label: 'Tickets', value: ticketAssignedCount, color: '#0ea5e9' },
    { label: 'Speakers', value: Object.values(speakerCounts).reduce((a, b) => a + b, 0) || fallbackSpeakerCount, color: '#f59e42' },
    { label: 'Staff', value: staffCreatedCount, color: '#eab308' },
    { label: 'Revenue', value: analytics.totalRevenueUSD ?? analytics.totalRevenue, color: '#16a34a', prefix: 'USD ' },
    { label: 'Payments', value: analytics.totalPayments, color: '#6366f1' },
    { label: 'Staff Actions', value: analytics.totalStaffActions, color: '#eab308' },
  ];

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4, px: { xs: 1, md: 0 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reports & Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {analyticsSourceOptions.map(opt => (
            <Button
              key={opt.value}
              variant={analyticsSource === opt.value ? 'contained' : 'outlined'}
              onClick={() => setAnalyticsSource(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
          {analyticsSource === 3 && (
            <>
              <Box sx={{ minWidth: 160 }}>
                <TextField
                  select
                  label="Filter By"
                  value={customFilter}
                  onChange={e => { setCustomFilter(e.target.value); setCustomFilterValue(''); }}
                  SelectProps={{ native: true }}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <option value="">Select</option>
                  {customFilterOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </TextField>
              </Box>
              {customFilter && (
                <Box sx={{ minWidth: 160 }}>
                  <TextField
                    label={(() => {
                      switch (customFilter) {
                        case 'paid': return 'Enter Paid Status (e.g. paid/unpaid)';
                        case 'pending': return 'Enter Pending Status (e.g. pending)';
                        case 'verified': return 'Enter Verified Status (e.g. true/false)';
                        case 'packageType': return 'Enter Package Type (e.g. VIP, Regular)';
                        case 'packageName': return 'Enter Package Name (e.g. Gold, Silver)';
                        default: return 'Value';
                      }
                    })()}
                    placeholder={(() => {
                      switch (customFilter) {
                        case 'paid': return 'paid/unpaid';
                        case 'pending': return 'pending';
                        case 'verified': return 'true/false';
                        case 'packageType': return 'VIP, Regular';
                        case 'packageName': return 'Gold, Silver';
                        default: return 'Value';
                      }
                    })()}
                    value={customFilterValue}
                    onChange={e => setCustomFilterValue(e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  />
                </Box>
              )}
              <Button
                variant="contained"
                sx={{ ml: 1 }}
                onClick={fetchAnalytics}
                disabled={!customFilter || !customFilterValue || loading}
              >
                {loading ? 'Loading...' : 'Apply Filter'}
              </Button>
            </>
          )}
        </Box>
        {error && (
          <Typography color="error" mb={2}>{error}</Typography>
        )}
        {/* Feedback for empty results */}
        {!loading && !error && analyticsSource === 3 && customFilter && customFilterValue && analytics && Object.keys(analytics.bookings || {}).length === 0 && (
          <Typography color="warning.main" mb={2}>No data found for this filter.</Typography>
        )}
        {/* KPI Cards */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {kpis.map((kpi, idx) => (
            <Grid item xs={6} sm={4} md={2} key={idx}>
              <Card sx={{
                background: isDark ? theme.palette.background.paper : '#fff',
                borderRadius: 2,
                boxShadow: isDark ? '0 1px 4px #2224' : '0 1px 4px #e6e9ee',
                color: isDark ? theme.palette.text.primary : '#222',
                minHeight: 60,
                px: 1,
                py: 0.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Typography sx={{ color: kpi.color, fontWeight: 700, fontSize: 13, textAlign: 'center' }}>{kpi.label}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, textAlign: 'center' }}>{kpi.prefix || ''}{kpi.value ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Booking Status Breakdown */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Booking Status Breakdown</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(bookingStatusCounts).map(([status, count], idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip label={status} sx={{ background: statusColors[status] || statusColors.default, color: '#fff', fontWeight: 700 }} size="small" />
                  </TableCell>
                  <TableCell>{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {/* Ticket Assignment & Speaker Counts */}
        <Box sx={{ mb: 3, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Ticket Assignment */}
          {ticketAssignedCount > 0 && (
            <Box sx={{ minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Ticket Assignment</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#0ea5e9' }}>Assigned: {ticketAssignedCount}</Typography>
            </Box>
          )}
          {/* Speakers per Package Type */}
          {Object.keys(packageTypeCounts).length > 0 && (
            <Box sx={{ minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Speakers per Package Type</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(packageTypeCounts).map(([type, count], idx) => (
                    <TableRow key={idx}>
                      <TableCell>{type}</TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {/* Speakers per Package Name */}
          {Object.keys(packageNameCounts).length > 0 && (
            <Box sx={{ minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Speakers per Package Name</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(packageNameCounts).map(([name, count], idx) => (
                    <TableRow key={idx}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={fetchAnalytics}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              boxShadow: isDark ? '0 2px 8px #2224' : '0 2px 8px #e6e9ee',
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            Filter
          </Button>
        </Box>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Booking Funnel" />
          <Tab label="Payments" />
          <Tab label="Staff Performance" />
        </Tabs>
        {/* Booking Funnel Tab */}
        {tab === 0 && bookingFunnelData && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: 400 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Booking Funnel</Typography>
              <Bar data={bookingFunnelData} options={{
                responsive: true,
                plugins: {
                  legend: { display: true, position: 'top' },
                  tooltip: { enabled: true }
                },
                scales: {
                  x: { grid: { color: '#eee' } },
                  y: { grid: { color: '#eee' } }
                }
              }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', maxWidth: 250 }}>
              <Pie data={bookingFunnelData} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 220 }}>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(analytics.bookings || {}).map(([status, count], idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Chip label={status} sx={{ background: chartColors[idx % chartColors.length], color: '#fff', fontWeight: 700 }} size="small" />
                      </TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setExportDialog(true)}>Export Data</Button>
            </Box>
          </Box>
        )}
        {/* Payments Tab */}
        {tab === 1 && revenueTrendsData && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: 400 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Payments</Typography>
              <Bar data={revenueTrendsData} options={{
                responsive: true,
                plugins: {
                  legend: { display: true, position: 'top' },
                  tooltip: { enabled: true }
                },
                scales: {
                  x: { grid: { color: '#eee' } },
                  y: { grid: { color: '#eee' } }
                }
              }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', maxWidth: 250 }}>
              <Pie data={revenueTrendsData} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 220 }}>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(analytics.payments || {}).map(([status, count], idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Chip label={status} sx={{ background: chartColors[idx % chartColors.length], color: '#fff', fontWeight: 700 }} size="small" />
                      </TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setExportDialog(true)}>Export Data</Button>
            </Box>
          </Box>
        )}
        {/* Staff Performance Tab */}
        {tab === 2 && staffPerformanceData && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: 400 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Staff Performance</Typography>
              <Bar data={staffPerformanceData} options={{
                responsive: true,
                plugins: {
                  legend: { display: true, position: 'top' },
                  tooltip: { enabled: true }
                },
                scales: {
                  x: { grid: { color: '#eee' } },
                  y: { grid: { color: '#eee' } }
                }
              }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', maxWidth: 250 }}>
              <Pie data={staffPerformanceData} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 220 }}>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Staff</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>Last Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.staffProcessLogs && analytics.staffProcessLogs.map((log, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <MuiTooltip title={log.status || ''} arrow>
                          <Chip label={log.userId} sx={{ background: chartColors[idx % chartColors.length], color: '#fff', fontWeight: 700 }} size="small" />
                        </MuiTooltip>
                      </TableCell>
                      <TableCell>{log.actions}</TableCell>
                      <TableCell>{log.lastAction}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setExportDialog(true)}>Export Data</Button>
            </Box>
          </Box>
        )}
        {/* Export Dialog */}
        <Dialog open={exportDialog} onClose={() => setExportDialog(false)}>
          <DialogTitle sx={{ fontWeight: 700, color: isDark ? theme.palette.primary.light : theme.palette.primary.main }}>
            Export Options
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2, color: isDark ? theme.palette.text.secondary : '#444' }}>
              Download the current data as PDF or Excel for further analysis or sharing.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2, fontWeight: 700, borderRadius: 2, px: 3 }}
              onClick={() => handleExport('PDF')}
            >
              Export as PDF
            </Button>
            <Button
              variant="contained"
              color="secondary"
              sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
              onClick={() => handleExport('Excel')}
            >
              Export as Excel
            </Button>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExportDialog(false)}
              sx={{ fontWeight: 700, color: isDark ? theme.palette.error.light : theme.palette.error.main }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {error && <Typography color="error" mb={2}>{error}</Typography>}
        {loading && <Typography>Loading...</Typography>}
      </Box>
    </SuperAdminLayout>
  );
}

export default ReportsDashboard;
